import { format } from "date-fns";
import { useMemo } from "react";

import { datetimeToJS } from "@/helpers/parsers";
import { cn } from "@/lib/utils";

import Moon from "./moon";
import type { TimeslotType } from "./types";

interface ItemProps {
  ignoreTimezone: boolean;
  isSelected: boolean;
  onClick(id: string): void;
  timeslot: TimeslotType;
}

function Item({ ignoreTimezone, isSelected, onClick, timeslot }: ItemProps) {
  const date = useMemo(
    () => datetimeToJS(timeslot.datetime, ignoreTimezone),
    [ignoreTimezone, timeslot.datetime]
  );

  const handleClick = () => onClick(timeslot.id);

  let className = "opacity-20";
  let props: React.ComponentProps<"div"> = {};

  if (isSelected) {
    className = "bg-white cursor-pointer";
    props = { onClick: handleClick };
  } else if (timeslot.available) {
    className =
      "cursor-pointer hover:bg-white/10 hover:border-dark-silver hover:shadow-0-0-10 shadow-white/20";
    props = { onClick: handleClick };
  }

  return (
    <div
      className={cn(
        "relative flex h-[35px] items-center justify-center gap-0.5 rounded-[4px] border border-cool-gray/70 bg-white/5 shadow-0-0-4 shadow-black/10 transition-[colors,shadow]",
        className
      )}
      {...props}
    >
      <span
        className={cn(
          "text-13 md:text-14",
          isSelected ? "text-black" : "text-white"
        )}
      >
        {format(date, "h:mm")}
      </span>
      <span className="ml-1 text-11 text-battleship-gray md:ml-0 md:text-12">
        {format(date, "aaa")}
      </span>
      <Moon date={date} />
    </div>
  );
}

interface DayViewProps {
  ignoreTimezone: boolean;
  onClick(id: string): void;
  timeslotId?: string;
  timeslots: TimeslotType[];
}

export default function DayView({
  ignoreTimezone,
  onClick,
  timeslotId,
  timeslots,
}: DayViewProps) {
  return (
    <div className="grid grid-cols-4 gap-1">
      {timeslots.map((timeslot) => (
        <Item
          ignoreTimezone={ignoreTimezone}
          isSelected={timeslot.id === timeslotId}
          key={timeslot.id}
          onClick={onClick}
          timeslot={timeslot}
        />
      ))}
    </div>
  );
}
